import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { getError, UnauthenticatedError } from "../model/errors/errors";

export class Api {
  private api: AxiosInstance;

  public constructor(config: AxiosRequestConfig) {
    this.api = axios.create(config);

    this.api.interceptors.request.use((value: InternalAxiosRequestConfig) => {
      const config = {
        ...value,
        timeout: parseInt(import.meta.env.VITE_APP_REQUEST_TIMEOUT_MS as string),
        baseURL: import.meta.env.VITE_APP_API_URL,
      };

      if (localStorage.getItem("token")) {
        config.headers.Authorization = `Bearer ${localStorage.getItem(
          "token"
        )}`;
      }

      return {
        ...config,
      };
    });

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.log(error);
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.status >= 200 && response.status < 300
            && (response.status == 204 || !response.data.status_code || response.data.status_code == 200)) {
          return response.data;
        }
        return Promise.reject({...response.data, status_code: response.data.status_code ?? response.status});
      },
      (error) => {
        const err = getError(error);
        if (err instanceof UnauthenticatedError) {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
        return Promise.reject(err.serialize());
      }
    );
  }

  public getUri(config?: AxiosRequestConfig): string {
    return this.api.getUri(config);
  }

  public request<T, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.api.request(config);
  }

  public get<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.get(url, config);
  }

  public delete<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.delete(url, config);
  }

  public head<T, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.head(url, config);
  }

  public post<T, D, R = AxiosResponse<T>>(
    url: string,
    data: D,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.post(url, data, config);
  }

  public put<T, D, R = AxiosResponse<T>>(
    url: string,
    data: D,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.put(url, data, config);
  }

  public patch<T, R = AxiosResponse<T>>(
    url: string,
    data?: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.patch(url, data, config);
  }

  /**
   *
   * @param data Object of params
   * @returns
   */
  public encodeQueryParams(data: object): string {
    return Object.entries(data)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");
  }
}
